import {
  Room,
  ParticipantEvent,
  RoomEvent,
  RemoteTrackPublication,
  VideoQuality,
  LocalVideoTrack,
  isVideoTrack,
  Track,
} from 'livekit-client';
import * as React from 'react';
import { applyIncomingVideoQualitySkippingScreenShare } from '@/lib/screen-share-quality-guard';
import { notifyBackgroundPaused, notifyProcessorCpuConstrained } from '@/lib/background-processor';
import { applyLocalVideoTrackPerformanceCap } from '@/lib/camera-publish-bitrate';

export type LowCPUOptimizerOptions = {
  reducePublisherVideoQuality: boolean;
  reduceSubscriberVideoQuality: boolean;
  disableVideoProcessing: boolean;
};

const defaultOptions: LowCPUOptimizerOptions = {
  reducePublisherVideoQuality: true,
  reduceSubscriberVideoQuality: true,
  disableVideoProcessing: false,
} as const;

/**
 * This hook ensures that on devices with low CPU, the performance is optimised when needed.
 * This is done by primarily reducing the video quality to low when the CPU is constrained.
 */
export function useLowCPUOptimizer(room: Room, options: Partial<LowCPUOptimizerOptions> = {}) {
  const [lowPowerMode, setLowPowerMode] = React.useState(false);
  const opts = React.useMemo(() => ({ ...defaultOptions, ...options }), [options]);
  React.useEffect(() => {
    const handleCpuConstrained = async (track: LocalVideoTrack) => {
      setLowPowerMode(true);
      console.warn('Local track CPU constrained', track);
      if (opts.reducePublisherVideoQuality) {
        // Cap de bitrate/fps mantendo as camadas de simulcast. prioritizePerformance()
        // do SDK desliga 2 camadas e o SFU marca o publisher como Poor (vermelho).
        // Com fundo virtual ativo, o processor desce um tier (captura + bitrate) e
        // para de tentar recuperar; sem processor, aplica o cap de performance direto.
        void notifyProcessorCpuConstrained(track).then((handled) => {
          if (!handled) {
            return applyLocalVideoTrackPerformanceCap(track);
          }
          return true;
        });
      }
      if (opts.disableVideoProcessing && isVideoTrack(track)) {
        track.stopProcessor();
        notifyBackgroundPaused();
      }
      if (opts.reduceSubscriberVideoQuality) {
        applyIncomingVideoQualitySkippingScreenShare(room, VideoQuality.LOW);
      }
    };

    room.localParticipant.on(ParticipantEvent.LocalTrackCpuConstrained, handleCpuConstrained);
    return () => {
      room.localParticipant.off(ParticipantEvent.LocalTrackCpuConstrained, handleCpuConstrained);
    };
  }, [room, opts.reducePublisherVideoQuality, opts.reduceSubscriberVideoQuality, opts.disableVideoProcessing]);

  React.useEffect(() => {
    const lowerQuality = (_: unknown, publication: RemoteTrackPublication) => {
      if (publication.kind !== Track.Kind.Video) {
        return;
      }
      if (publication.source === Track.Source.ScreenShare) {
        publication.setVideoQuality(VideoQuality.HIGH);
        return;
      }
      publication.setVideoQuality(VideoQuality.LOW);
    };
    if (lowPowerMode && opts.reduceSubscriberVideoQuality) {
      room.on(RoomEvent.TrackSubscribed, lowerQuality);
    }

    return () => {
      room.off(RoomEvent.TrackSubscribed, lowerQuality);
    };
  }, [lowPowerMode, room, opts.reduceSubscriberVideoQuality]);

  return lowPowerMode;
}
